import { Badge, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconBuildingStore,
  IconCategory,
  IconPencil,
  IconRepeat,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { ExpenseItem } from 'src/Components/Expenses/ExpenseItem';
import { ShareSettlementStatus } from 'src/Components/Expenses/ShareSettlementStatus';
import { AttachmentsViewer } from 'src/Components/Shared/AttachmentsViewer';
import { CopyTextButton } from 'src/Components/Shared/CopyTextButton';
import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useDeleteExpense } from 'src/Hooks/Mutations/useDeleteExpense';
import { useStopExpenseRecurrence } from 'src/Hooks/Mutations/useStopExpenseRecurrence';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { AssetDetailsDtoCurrentUserRoleEnum } from 'src/Services/API/generated';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './ExpenseDetails.module.scss';
import { Props } from './ExpenseDetails.types';

const ExpenseDetails = ({ expense, onDelete }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigateKeepingSearch();
  const { mutate: onDeleteExpense } = useDeleteExpense({ onSuccess: onDelete });
  const { mutate: stopRecurrence } = useStopExpenseRecurrence();

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const { data: user } = useFetchUser();
  const { data: asset } = useFetchAssetDetails();
  const isPayer = !!expense.paidBy && expense.paidBy.id === user?.id;
  const isManager = asset?.currentUserRole === AssetDetailsDtoCurrentUserRoleEnum.Manager;
  const canRemindShares = isPayer || isManager;

  const handleStopRecurrence = () => {
    modals.openConfirmModal({
      title: t('expenses.stopRecurrence.title', 'Arrêter la récurrence'),
      children: t(
        'expenses.stopRecurrence.confirm',
        'Les prochaines occurrences ne seront plus générées. Les dépenses déjà créées sont conservées.'
      ),
      labels: {
        confirm: t('expenses.stopRecurrence.confirmButton', 'Arrêter'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'orange' },
      onConfirm: () => stopRecurrence(expense.id),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.line}>
        <div className={styles.title}>{expense.title}</div>
        {!expense.isReimbursed && (
          <RowActions
            actions={[
              {
                key: 'edit',
                label: t('expenses.menu.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                disabled: assetAboveQuotas,
                onClick: () =>
                  navigate(`edit`, { state: { disableAnimation: true }, replace: true }),
              },
              {
                key: 'delete',
                label: t('expenses.menu.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                onClick: () => onDeleteExpense(expense.id),
              },
            ]}
          />
        )}
      </div>
      <div className={styles.properties}>
        <KeyValueLine
          keyIcon={<IconCategory className={styles.icon} size={16} />}
          propertyKey={t('expense.details.category', 'Catégorie')}
          value={<Badge className={styles.badge}>{t(`expense.type.${expense.type}`)}</Badge>}
        />
        <KeyValueLine
          keyIcon={<IconBuildingStore className={styles.icon} size={16} />}
          propertyKey={t('expense.details.merchant', 'Enseigne')}
          value={expense.merchant}
        />
      </div>

      <>
        <div className={styles.subtitle}>{t('expenses.details.paidBy', 'Payé par')}</div>
        <ExpenseItem
          username={
            (expense.paidByContact
              ? expense.paidByContact.displayName
              : expense.paidByPlaceholderMember
                ? [
                    expense.paidByPlaceholderMember.firstName,
                    expense.paidByPlaceholderMember.lastName,
                  ].join(' ')
                : expense.paidBy
                  ? [expense.paidBy.firstName, expense.paidBy.lastName].join(' ')
                  : expense.paidByBankAccount?.name) ?? ''
          }
          amount={expense.amount}
          user={expense.paidBy}
          badge={
            expense.paidByContact ? (
              <Badge className={styles.contactBadge}>
                {t('expense.details.contactBadge', 'Contact')}
              </Badge>
            ) : undefined
          }
        />
      </>
      {expense.reimburseToBankAccount && (
        <>
          <div className={styles.subtitle}>
            {t('expenses.details.reimburseVia', 'Rembourser via')}
          </div>
          <ExpenseItem username={expense.reimburseToBankAccount.name} amount={expense.amount} />
        </>
      )}
      <div className={styles.subtitle}>{t('expenses.details.involved', 'Participants')}</div>
      <div className={styles.shares}>
        {expense.shares.map(share => {
          // A share is borne by a registered user or by an unregistered co-owner, never both.
          const bearer = share.user ?? share.placeholderMember;
          const showReminder = canRemindShares && !share.settledAt && bearer;
          return (
            <div key={share.id} className={styles.shareRow}>
              <div className={styles.shareItem}>
                <ExpenseItem
                  username={[bearer?.firstName, bearer?.lastName].filter(Boolean).join(' ')}
                  amount={share.amount}
                  user={share.user ?? undefined}
                  subtitle={<ShareSettlementStatus share={share} />}
                />
              </div>
              {showReminder && (
                <CopyTextButton
                  text={t(
                    'expenses.share.reminderText',
                    'Bonjour {{firstName}}, il reste {{amount}} à me rembourser pour {{title}} ({{date}}). Merci !',
                    {
                      firstName: bearer.firstName ?? '',
                      amount: formatEuros(share.amount),
                      title: expense.title,
                      date: expense.paidAt.format('DD/MM/YYYY'),
                    }
                  )}
                  mixpanelSource="expense_share"
                  label={t('expenses.share.remindLabel', 'Copier le message pour relancer')}
                />
              )}
            </div>
          );
        })}
      </div>

      {expense.note && (
        <>
          <div className={styles.subtitle}>
            {t('expenses.details.note', "Détails de l'opération")}
          </div>
          <div className={styles.note}>{expense.note}</div>
        </>
      )}

      {expense.attachments && expense.attachments.length > 0 && (
        <>
          <div className={styles.subtitle}>{t('expenses.details.receipts', 'Justificatifs')}</div>
          <AttachmentsViewer attachments={expense.attachments} />
        </>
      )}

      {expense.isRecurring && (
        <Button
          className={styles.stopRecurrenceButton}
          variant="light"
          color="orange"
          leftSection={<IconRepeat size={16} />}
          onClick={handleStopRecurrence}
        >
          {t('expenses.stopRecurrence.button', 'Arrêter la récurrence')}
        </Button>
      )}

      <LinkedEntitiesSection
        entityType={LinkableEntityType.Expense}
        entityId={expense.id}
        entityTitle={expense.title}
      />
    </div>
  );
};

export default ExpenseDetails;
